import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/auth.store'
import { authService } from '@/services/auth.service'

const WARNING_MS = 14 * 60 * 1000
const TIMEOUT_MS = 15 * 60 * 1000

export function useInactivityTimeout() {
  const navigate = useNavigate()
  const { isAuthenticated, logout } = useAuthStore()
  const [showWarning, setShowWarning] = useState(false)
  const warnTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const logoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const reset = useCallback(() => {
    setShowWarning(false)
    if (warnTimer.current) clearTimeout(warnTimer.current)
    if (logoutTimer.current) clearTimeout(logoutTimer.current)
    if (!isAuthenticated) return

    warnTimer.current = setTimeout(() => setShowWarning(true), WARNING_MS)
    logoutTimer.current = setTimeout(async () => {
      try { await authService.logout() } catch { /* ignore */ }
      logout()
      navigate('/login?reason=inactive', { replace: true })
    }, TIMEOUT_MS)
  }, [isAuthenticated, logout, navigate])

  useEffect(() => {
    if (!isAuthenticated) return
    const events = ['mousemove', 'keydown', 'click', 'touchstart', 'scroll'] as const
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }))
    reset()
    return () => {
      events.forEach((e) => window.removeEventListener(e, reset))
      if (warnTimer.current) clearTimeout(warnTimer.current)
      if (logoutTimer.current) clearTimeout(logoutTimer.current)
    }
  }, [isAuthenticated, reset])

  return { showWarning, stayActive: reset }
}
